import type { EditorPlugin } from 'modules/fabric-editor';
import { FabricEditor, type FabricEditorOptions, type Resolution } from 'modules/fabric-editor';
import React, { useLayoutEffect, useRef } from 'react';

export interface FabricEditorCanvasProps {
  width?: number;
  height?: number;
  resolution?: Resolution;
  fabricOptions?: FabricEditorOptions['fabricOptions'];
  plugins?: EditorPlugin[];
  onReady?: (editor: FabricEditor) => void;
  autoFit?: boolean;
  className?: string;
}

export const FabricEditorCanvas: React.FC<FabricEditorCanvasProps> = ({
  width,
  height,
  resolution,
  fabricOptions,
  plugins,
  onReady,
  autoFit,
  className,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const editorRef = useRef<FabricEditor | null>(null);

  const stretch = width === undefined && height === undefined;
  const effectiveAutoFit = autoFit ?? stretch;

  useLayoutEffect(() => {
    if (!canvasRef.current || !wrapperRef.current) return;

    let initialWidth = width ?? 0;
    let initialHeight = height ?? 0;
    if (stretch) {
      const rect = wrapperRef.current.getBoundingClientRect();
      initialWidth = rect.width;
      initialHeight = rect.height;
    }

    canvasRef.current.width = initialWidth;
    canvasRef.current.height = initialHeight;

    const instance = new FabricEditor({
      canvasEl: canvasRef.current,
      resolution,
      fabricOptions,
      autoFit: effectiveAutoFit,
    });
    editorRef.current = instance;

    if (plugins) for (const p of plugins) instance.use(p);
    onReady?.(instance);

    let observer: ResizeObserver | undefined;
    if (stretch && typeof ResizeObserver !== 'undefined') {
      let lastW = initialWidth;
      let lastH = initialHeight;
      observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const { width: w, height: h } = entry.contentRect;
        if (w <= 0 || h <= 0) return;
        if (Math.abs(w - lastW) < 0.5 && Math.abs(h - lastH) < 0.5) return;
        lastW = w;
        lastH = h;
        instance.setSize(w, h);
      });
      observer.observe(wrapperRef.current);
    }

    return () => {
      observer?.disconnect();
      instance.dispose();
      editorRef.current = null;
    };
    // Plugins/resolution/fabricOptions/autoFit are intentionally captured at mount;
    // re-mount the component to apply new ones.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wrapperStyle: React.CSSProperties = stretch
    ? { width: '100%', height: '100%', position: 'relative' }
    : {};

  return (
    <div ref={wrapperRef} className={className} style={wrapperStyle}>
      <canvas ref={canvasRef} />
    </div>
  );
};
